import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import Logo from '@comp/Logo/Logo';
import Divider from '@base/Divider/Divider';
import useTimeout from '@hooks/useTimeout';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Dynamic from '@base/Dynamic/Dynamic';
import useEducatorContext from '@hooks/useEducatorContext';
import useWindowSize from '@hooks/useWindowSize';
import { useFlagSelector } from '@hooks/useLayoutOptions';
import HeaderTop from '../Components/HeaderContainer';
import HeaderContent from '../Components/HeaderParent';
import HeaderUser from './HeaderUser';
import Actions from './Actions';
import PaymentNudge from '../PaymentLayout/PaymentNudge';
import HeaderNotification from './HeaderNotification';

const GoalSelector = Dynamic(() => import('./GoalSelector/HeaderGoalSelector'));
const SearchTab = Dynamic(() => import('./SearchTab'));

const HeaderContainer = styled(HeaderTop)`
  background: ${({ transparent, hideBgColor, theme }) =>
    transparent || hideBgColor ? 'none' : theme.colors.white};
  box-shadow: ${({ transparent, hideBgColor, theme, hideBoxShadow }) =>
    transparent || hideBgColor || hideBoxShadow
      ? 'initial'
      : boxShadowGenerator({ theme })};
  position: ${({ transparent }) => (transparent ? 'absolute' : 'sticky')};
  @media (max-width: 768px) {
    display: ${({ $headerDisplay }) => ($headerDisplay ? 'block' : 'none')};
    height: 72px;
  }
  @media only screen and (max-width: 47.95em) {
    box-shadow: inset 0px -1px 0px #f0f4f7;
    display: block;
    width: 100%;
    height: 48px;
  }
`;

const HeaderParent = styled(HeaderContent)`
  @media (max-width: 768px) {
    width: 100%;
    padding: 0 16px;
  }
  @media only screen and (max-width: 540px) {
    display: flex;
    width: 100%;
    padding: 16px;
    justify-content: space-between;
  }
`;

const StyledDivider = styled(Divider)`
  &.MuiDivider-root {
    margin: auto;
    height: 40px;
  }
  @media (max-width: 47.95em) {
    display: none;
  }
`;

const isSubscriptionPricingsUrl = (route) =>
  route === '/goal/[goalSlug]/[goalUID]/subscribe';

const headerUrls = ['/manage-goals', '/goal/[goalSlug]/[goalUID]'];
const hideLoginButtonUrls = [
  '/goal/[goalSlug]/[goalUID]',
  '/course/[slug]/[courseUID]',
  '/lesson/[lessonSlug]/[lessonUID]'
];

const Header = ({
  transparent,
  hideSearch,
  isCheckoutUrl,
  toggleDarkMode,
  isDarkModeOn,
  isLoginUrl
}) => {
  const isEducatorSide = useEducatorContext();
  const router = useRouter();
  const { route } = router;
  const isNotificationsRoute = route === '/notifications';
  const [mounted, setMounted] = useState(false);
  const isLoggedIn = useSelector(({ auth }) => auth.isLoggedIn);
  const { isMobile } = useWindowSize();
  useTimeout(() => setMounted(true), 0);

  const hideGoalSelector = useFlagSelector('hideGoalSelector');

  const isProdigyUrl =
    route === '/prodigy' ||
    route === '/event/UCL' ||
    route === '/event/testseries/aspire' ||
    route === '/event/testseries/[testSeriesKey]';

  const isGoalOnBoardingPreferenceUrl =
    route === '/goal/[goalSlug]/[goalUID]/preferences';

  const displayNotification =
    isLoggedIn &&
    mounted &&
    !isNotificationsRoute &&
    !isEducatorSide &&
    !isCheckoutUrl &&
    !isProdigyUrl &&
    !isGoalOnBoardingPreferenceUrl;

  const displayGoalSelector =
    !isCheckoutUrl &&
    !hideGoalSelector &&
    !isEducatorSide &&
    !(isMobile && isLoginUrl) &&
    !isGoalOnBoardingPreferenceUrl;

  const displaySearch =
    !hideSearch &&
    !isCheckoutUrl &&
    !isEducatorSide &&
    mounted &&
    !(isMobile && isLoginUrl) &&
    !isGoalOnBoardingPreferenceUrl;

  const isMWUrl = route === '/login' || route === '/about';

  if (isMobile && isMWUrl) {
    return (
      <HeaderContainer transparent={transparent}>
        <HeaderParent>
          <Logo isReload />
        </HeaderParent>
      </HeaderContainer>
    );
  }
  const showHeader = headerUrls.includes(route);
  const hideLoginButton = hideLoginButtonUrls.includes(route);
  const renderLoginAction = () => {
    if (hideLoginButton && isMobile) return null;
    return <Actions />;
  };
  return (
    <HeaderContainer
      transparent={transparent}
      hideBoxShadow={isGoalOnBoardingPreferenceUrl}
      $headerDisplay={showHeader}
    >
      <HeaderParent>
        {isCheckoutUrl && !isSubscriptionPricingsUrl(route) ? (
          <Logo isClassroom={route.includes('classroom')} disabled />
        ) : (
          <Logo
            isClassroom={route.includes('classroom')}
            isReload={(isProdigyUrl || isLoginUrl) && isMobile}
          />
        )}
        {displayGoalSelector && <GoalSelector />}
        {displaySearch && <SearchTab isLoggedIn={isLoggedIn} />}
        {displayNotification && <HeaderNotification />}
        {isMobile ? null : <PaymentNudge />}
        {!isCheckoutUrl && !isLoginUrl && (
          <>
            {isLoggedIn &&
              !isEducatorSide &&
              !isGoalOnBoardingPreferenceUrl && (
                <StyledDivider orientation="vertical" />
              )}
            {isLoggedIn ? (
              <HeaderUser
                toggleDarkMode={toggleDarkMode}
                isDarkModeOn={isDarkModeOn}
                isEducatorUrl={isEducatorSide}
              />
            ) : (
              renderLoginAction()
            )}
          </>
        )}
      </HeaderParent>
    </HeaderContainer>
  );
};

export default Header;

Header.propTypes = {
  isCheckoutUrl: PropTypes.bool,
  isDarkModeOn: PropTypes.bool,
  toggleDarkMode: PropTypes.func.isRequired,
  hideSearch: PropTypes.bool,
  transparent: PropTypes.bool,
  isLoginUrl: PropTypes.bool
};

Header.defaultProps = {
  isCheckoutUrl: false,
  isDarkModeOn: false,
  hideSearch: false,
  transparent: false,
  isLoginUrl: false
};
